import { LinkingType, OptionGroupDto, OptionGroupLinkStatus } from '@/types';
import { request } from '@/utils/ajax';

export interface RoomLinkUpdateParams {
    roomId: number;
    pricingFieldId: number | null;
}

export type OptionGroupSearchParams = {
    projectId: number;
    rooms?: number[];
    roomIds?: number[];
    type?: LinkingType;
    searchText?: string;
    tagIds?: number[];
    deadlineIds?: number[];
    itemCategoryIds?: number[];
    providedByProfileIds?: number[];
    publishedStatus?: OptionGroupLinkStatus[];
    pricingFieldIds?: number[];
    unitIds?: number[];
};

export function getOptionGroup(optionGroupId: number): Promise<OptionGroupDto> {
    return request(`api/option-group/${optionGroupId}`).then((x) => x.json());
}

/** Units, room tags and option items of a group that have a live order and therefore cannot be unlinked/removed. */
export interface OptionGroupOrderLocks {
    unitIds: number[];
    roomIds: number[];
    optionItemIds: number[];
}

export function getOptionGroupOrderLocks(optionGroupId: number): Promise<OptionGroupOrderLocks> {
    return request(`api/option-group/${optionGroupId}/order-locks`).then((x) => x.json());
}

export function searchOptionGroups(options: OptionGroupSearchParams): Promise<OptionGroupDto[]> {
    return request(`api/option-group`, { query: options }).then((x) => x.json());
}

export interface OptionGroupUpdateParams {
    id?: number;
    name: string;
    /** Internal note shown only to sellers/admins, never to buyers. */
    internalNote?: string | null;
    description: string;
    providedByProfileId: number | null;
    projectId: number;
    allowManualQuantity: boolean;
    disableManualSelection: boolean;
    showQuantityToBuyers: boolean;
    hidePrice: boolean;
    makeFree: boolean;
    projectFeeSetId: number;
    deadlineId: number;
    /** Optional non-room project tag to associate with the option group. Null clears the tag. */
    projectTagId: number | null;
}

export interface DirectOptionGroupUpdateParams extends OptionGroupUpdateParams {
    pricingVariableId: number | null;
    roomId: number;
}
export interface RoomOptionGroupUpdateParams extends OptionGroupUpdateParams {}

export function changeDirectOptionGroupStatus(optionGroupid: number, options: DirectLinkStatusUpdateParams): Promise<any> {
    return request(`api/option-group/direct/${optionGroupid}/status`, { method: 'put', body: options, json: true });
}

export function changeRoomOptionGroupStatus(optionGroupid: number, options: RoomLinkStatusUpdateParams): Promise<any> {
    return request(`api/option-group/room/${optionGroupid}/status`, { method: 'put', body: options, json: true });
}

export function updateDirectOptionGroup(optionGroupid: number, options: DirectOptionGroupUpdateParams): Promise<any> {
    return request(`api/option-group/direct/${optionGroupid}`, { method: 'put', body: options, json: true });
}

export function updateRoomOptionGroup(optionGroupid: number, options: RoomOptionGroupUpdateParams): Promise<any> {
    return request(`api/option-group/room/${optionGroupid}`, { method: 'put', body: options, json: true });
}

export function updateDirectOptionGroupLinks(optionGroupid: number, options: LinkToResidentialUnitsOptions): Promise<any> {
    return request(`api/option-group/direct/${optionGroupid}/links`, { method: 'put', body: options, json: true });
}

export function updateRoomOptionGroupLinks(optionGroupid: number, options: LinkToRoomsOptions): Promise<any> {
    return request(`api/option-group/room/${optionGroupid}/links`, { method: 'put', body: options, json: true });
}

export function deleteOptionGroup(optionGroupId: number): Promise<Response> {
    return request(`api/option-group/${optionGroupId}`, { method: 'delete' });
}

export function reorderOptionGroupsByRoom(roomId: number, sortedOptionGroupIds: number[]): Promise<any> {
    return request(`api/option-group/room/${roomId}/reorder`, { method: 'put', body: sortedOptionGroupIds, json: true });
}

export function setOptionGroupConfiguratorMapping(optionGroupId: number, configuratorMappingId: string | null): Promise<any> {
    return request(`api/option-group/${optionGroupId}/configurator-mapping`, { method: 'put', body: { configuratorMappingId }, json: true });
}

export interface DirectLinkStatusUpdateParams {
    residentialUnitIds: number[];
    status: OptionGroupLinkStatus;
    rejectReason: string;
    sendRejectEmail: boolean;
}

export interface RoomLinkStatusUpdateParams {
    roomIds: number[];
    status: OptionGroupLinkStatus;
    rejectReason: string;
    sendRejectEmail: boolean;
}

export interface LinkToRoomsOptions {
    links: RoomLinkUpdateParams[];
}

export interface LinkToResidentialUnitsOptions {
    idsToAdd: number[];
    idsToRemove: number[];
}
